import { useRef, useState } from 'react'
import type { Editor } from '@tiptap/core'
import type {
  ComplianceDocumentRewriteDocumentType,
  ComplianceDocumentSectionRecord,
} from '@artem-lex/compliance-documents'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import {
  useRecordComplianceDocumentRewriteOutcome,
  useRewriteInstruction,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { selectionIncludesTable } from '@/components/document-editor-bubble-menu'
import { readEditorSelectionMarkdown } from '../utils/complianceDocumentEditorUtils'
import { applyRewriteResultToEditor } from '../utils/applyRewriteResultToEditor'
import {
  readRewriteErrorDetails,
  resolveRewriteApiErrorAction,
} from './rewriteApiErrorHandling'

type RewriteSelectionSnapshot = {
  from: number
  to: number
  text: string
}

type UseComplianceDocumentRewriteParams = {
  complianceId: string
  documentType: ComplianceDocumentRewriteDocumentType
  sections: ComplianceDocumentSectionRecord[]
  translate: (key: string) => string
  getFlushedSectionMarkdown?: (sectionId: string) => string | null
  /** Called when the rewrite drawer opens (e.g. cancel pending editor autosave). */
  onOpenRewritePanel?: () => void
}

export function useComplianceDocumentRewrite({
  complianceId,
  documentType,
  sections,
  translate,
  getFlushedSectionMarkdown,
  onOpenRewritePanel,
}: UseComplianceDocumentRewriteParams) {
  const focusedEditorRef = useRef<Editor | null>(null)
  const focusedSectionIdRef = useRef<string | null>(null)
  const rewriteSelectionSnapshotRef = useRef<RewriteSelectionSnapshot | null>(
    null
  )
  const { mutateAsync: rewriteInstruction, isPending: isRewritePending } =
    useRewriteInstruction()
  const recordRewriteOutcome = useRecordComplianceDocumentRewriteOutcome()
  const [rewriteInstructionValue, setRewriteInstructionValue] = useState('')
  const [rewriteResult, setRewriteResult] = useState('')
  const [rewriteRange, setRewriteRange] = useState<{
    from: number
    to: number
  } | null>(null)
  const [rewriteRunId, setRewriteRunId] = useState<string | null>(null)
  const [isRewritePanelOpen, setIsRewritePanelOpen] = useState(false)
  const [selectedTextInPanel, setSelectedTextInPanel] = useState('')
  const [rewriteBlockedMessage, setRewriteBlockedMessage] = useState<
    string | null
  >(null)
  const [isInsufficientSourcesDialogOpen, setIsInsufficientSourcesDialogOpen] =
    useState(false)
  const [insufficientSourcesAvailableNames, setInsufficientSourcesAvailableNames] =
    useState<string[]>([])
  const [insufficientSourcesBlockReason, setInsufficientSourcesBlockReason] =
    useState<string | null>(null)
  const [isInstructionRefusedDialogOpen, setIsInstructionRefusedDialogOpen] =
    useState(false)

  const handleFocusedEditorChange = (
    editor: Editor | null,
    sectionId: string | null
  ) => {
    focusedEditorRef.current = editor
    focusedSectionIdRef.current = sectionId
  }

  const resetRewriteDraft = () => {
    setRewriteInstructionValue('')
    setRewriteResult('')
    setRewriteRange(null)
    setRewriteRunId(null)
  }

  const openRewritePanel = () => {
    const editor = focusedEditorRef.current
    if (editor == null) {
      return
    }
    const { from, to, empty } = editor.state.selection
    const text = empty
      ? ''
      : readEditorSelectionMarkdown(editor, from, to)
    const includesTable =
      !empty && selectionIncludesTable(editor.state, from, to)
    rewriteSelectionSnapshotRef.current =
      empty || includesTable ? null : { from, to, text }
    setSelectedTextInPanel(text)
    setRewriteBlockedMessage(
      includesTable ? translate('rewriteDisabledTableSelection') : null
    )
    resetRewriteDraft()
    onOpenRewritePanel?.()
    setIsRewritePanelOpen(true)
  }

  const closeRewritePanel = () => {
    rewriteSelectionSnapshotRef.current = null
    setIsRewritePanelOpen(false)
    resetRewriteDraft()
    setRewriteBlockedMessage(null)
  }

  const recordOutcome = (outcome: 'accepted' | 'rejected') => {
    if (rewriteRunId == null) {
      return
    }
    const runId = rewriteRunId
    recordRewriteOutcome.mutate(
      { runId, outcome },
      {
        onError: (error) => {
          Sentry.captureException(error, {
            tags: { errorId: 'compliance-document-rewrite-outcome' },
            extra: { runId, outcome },
          })
        },
      }
    )
  }

  const handleAcceptRewrite = () => {
    if (!rewriteResult || rewriteRange == null) {
      return
    }
    const editor = focusedEditorRef.current
    if (editor == null) {
      return
    }
    applyRewriteResultToEditor(editor, rewriteResult, rewriteRange)
    recordOutcome('accepted')
    rewriteSelectionSnapshotRef.current = null
    resetRewriteDraft()
    setRewriteBlockedMessage(null)
    setIsRewritePanelOpen(false)
  }

  const handleRejectRewrite = () => {
    recordOutcome('rejected')
    rewriteSelectionSnapshotRef.current = null
    resetRewriteDraft()
    setRewriteBlockedMessage(null)
    setIsRewritePanelOpen(false)
  }

  const handleRewrite = async () => {
    const editor = focusedEditorRef.current
    const sectionId = focusedSectionIdRef.current
    const selectionSnapshot = rewriteSelectionSnapshotRef.current
    if (editor == null || sectionId == null || selectionSnapshot == null) {
      return
    }
    const section = sections.find((entry) => entry.sectionId === sectionId)
    if (section == null) {
      return
    }
    const { from, to, text: selectedText } = selectionSnapshot
    if (selectedText.trim().length === 0) {
      return
    }
    const flushedSectionMarkdown =
      getFlushedSectionMarkdown?.(section.sectionId) ?? section.markdown
    setRewriteResult('')
    setRewriteRange(null)
    setRewriteRunId(null)
    setIsInstructionRefusedDialogOpen(false)
    setInsufficientSourcesAvailableNames([])
    setInsufficientSourcesBlockReason(null)
    try {
      const response = await rewriteInstruction({
        complianceId,
        documentType,
        sectionId: section.sectionId,
        sectionKind: section.kind,
        sectionMarkdown: flushedSectionMarkdown,
        originalText: selectedText,
        instruction: rewriteInstructionValue,
      })
      if (response.ok) {
        setRewriteResult(response.result ?? '')
        setRewriteRunId(response.runId)
        setRewriteRange({ from, to })
        return
      }
      applyRewriteOutcome(response)
    } catch (error) {
      applyRewriteOutcome(error)
    }
  }

  const applyRewriteOutcome = (error: unknown) => {
    const details = readRewriteErrorDetails(error)
    const action = resolveRewriteApiErrorAction(error)
    if (action.type === 'insufficient-sources-dialog') {
      setInsufficientSourcesAvailableNames(details.availableSourceNames ?? [])
      setInsufficientSourcesBlockReason(details.blockReason ?? null)
      setIsInsufficientSourcesDialogOpen(true)
      return
    }
    if (action.type === 'instruction-refused-dialog') {
      setIsInstructionRefusedDialogOpen(true)
      return
    }
    if (action.type === 'retry-toast' || action.type === 'rethrow') {
      toast.error(translate('rewriteFailed'))
    }
  }

  return {
    focusedEditorRef,
    handleFocusedEditorChange,
    rewriteInstructionValue,
    setRewriteInstructionValue,
    rewriteResult,
    isRewritePending,
    isRewritePanelOpen,
    selectedTextInPanel,
    rewriteBlockedMessage,
    isInsufficientSourcesDialogOpen,
    setIsInsufficientSourcesDialogOpen,
    insufficientSourcesAvailableNames,
    insufficientSourcesBlockReason,
    isInstructionRefusedDialogOpen,
    setIsInstructionRefusedDialogOpen,
    openRewritePanel,
    closeRewritePanel,
    handleAcceptRewrite,
    handleRejectRewrite,
    handleRewrite,
  }
}
